"use client";

import { useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { PaymentApi } from "@/openapi/generated";
import { ResponseError } from "@/openapi/generated/runtime";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";

export const useExternalTerminalsGet = (options?: { enabled?: boolean }) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const enabled =
    options?.enabled !== false && !!accessToken && tenantId > 0;

  const { data, isError, isLoading, isPending, isFetching, refetch } = useQuery({
    queryKey: [QueryKeys.ExternalTerminals, tenantId],
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(PaymentApi, accessToken);
      try {
        return await service.apiPaymentsExternalTerminalsGet({
          xTenantID: tenantId,
        });
      } catch (error) {
        if (
          error instanceof ResponseError &&
          (error.response.status === 401 ||
            error.response.status === 403 ||
            error.response.status === 404)
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return {
    terminals: data ?? [],
    isError,
    isLoading,
    isPending,
    isFetching,
    refetch,
  };
};
